import React from 'react'
import {
  List,
  useTable,
  ShowButton,
  EditButton,
  DeleteButton,
  DateField,
  CreateButton,
} from '@refinedev/antd'
import { useList } from '@refinedev/core'
import { Table, Space } from 'antd'
import type { Tables } from '@muaa/shared'
import { brand } from '../../theme/brand'
import { useRowShow, ListPage, Pill, TableSurface, type Stat } from '../../theme/listkit'
import { Sunrise, CalendarDays, FileText } from '../../components/icons'

type Devotional = Tables<'devotionals'>

export function DevotionalList() {
  const { tableProps } = useTable<Devotional>({
    syncWithLocation: true,
    sorters: {
      initial: [{ field: 'created_at', order: 'desc' }],
    },
  })
  const onRow = useRowShow('devotionals')

  // Bounded summary deck — two tiny count queries (pageSize:1) read straight off
  // PostgREST's `total` instead of pulling the whole `devotionals` table to the
  // client and filtering on the main thread.
  const monthBounds = React.useMemo(() => {
    const now = new Date()
    const start = new Date(now.getFullYear(), now.getMonth(), 1)
    const end = new Date(now.getFullYear(), now.getMonth() + 1, 1)
    const iso = (d: Date) =>
      `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
    return { start: iso(start), end: iso(end) }
  }, [])

  const { result: totalRes } = useList<Devotional>({
    resource: 'devotionals',
    pagination: { pageSize: 1 },
  })
  const total = totalRes?.total ?? 0

  const { result: monthRes } = useList<Devotional>({
    resource: 'devotionals',
    pagination: { pageSize: 1 },
    filters: [
      { field: 'date', operator: 'gte', value: monthBounds.start },
      { field: 'date', operator: 'lt', value: monthBounds.end },
    ],
  })
  const thisMonth = monthRes?.total ?? 0

  const stats: Stat[] = [
    { label: 'Total Devotionals', value: total.toLocaleString(), icon: Sunrise, tone: 'gold' },
    { label: 'This Month', value: thisMonth.toLocaleString(), hint: 'scheduled', icon: CalendarDays, tone: 'jaguar' },
    { label: 'Scripture', value: total ? 'Daily' : '—', icon: FileText, tone: 'green' },
  ]

  return (
    <ListPage
      intro={{
        eyebrow: 'Content · Devotionals',
        description:
          'The daily devotional rotation — a verse, a reflection, and a prayer for each day. Keep the spiritual reading current for members opening the app each morning.',
        actions: <CreateButton />,
      }}
      stats={stats}
      statColumns={3}
    >
      <List title={false} breadcrumb={false}>
        <TableSurface>
          <Table {...tableProps} rowKey="id" scroll={{ x: true }} onRow={onRow}>
            <Table.Column<Devotional>
              dataIndex="date"
              title="Date"
              render={(date: string | null) =>
                date ? (
                  <span style={{ fontFamily: brand.fSans, fontWeight: 600, color: brand.ink }}>{date}</span>
                ) : (
                  <span style={{ color: brand.sub }}>—</span>
                )
              }
            />
            <Table.Column<Devotional>
              dataIndex="title"
              title="Title"
              render={(title: string) => (
                <span style={{ fontFamily: brand.fDisplay, fontWeight: 700, color: brand.ink }}>{title}</span>
              )}
            />
            <Table.Column<Devotional>
              dataIndex="ref"
              title="Reference"
              render={(ref: string | null) =>
                ref ? <Pill tone="gold">{ref}</Pill> : <span style={{ color: brand.sub }}>—</span>
              }
            />
            <Table.Column<Devotional>
              dataIndex="verse"
              title="Verse (preview)"
              ellipsis
              render={(verse: string) => (
                <span style={{ fontFamily: brand.fSans, fontStyle: 'italic', color: brand.sub }}>
                  {verse.length > 80 ? `${verse.slice(0, 80)}…` : verse}
                </span>
              )}
            />
            <Table.Column<Devotional>
              dataIndex="created_at"
              title="Created"
              sorter
              render={(value: string) => <DateField value={value} format="MMM D, YYYY" />}
            />
            <Table.Column<Devotional>
              title="Actions"
              dataIndex="actions"
              render={(_: unknown, record: Devotional) => (
                <Space>
                  <ShowButton hideText size="small" recordItemId={record.id} />
                  <EditButton hideText size="small" recordItemId={record.id} />
                  <DeleteButton hideText size="small" recordItemId={record.id} />
                </Space>
              )}
            />
          </Table>
        </TableSurface>
      </List>
    </ListPage>
  )
}
